"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { signOut as localSignOut } from "next-auth/react";

import { useAuth } from "@/providers/AuthProvider";
import { Icon, IconName } from "../components/icons/Icon";
import { Image } from "../components/Image";

interface MenuProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    style?: React.CSSProperties;
}

export const Menu = ({ isOpen, setIsOpen, style }: MenuProps) => {
    const router = useRouter();
    const { user } = useAuth();
    const menuRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleLogout = async () => {
        try {
            // Call our remoteSignout API to handle Cognito signout
            await fetch("/api/auth/remoteSignout", { method: "POST" });
        } catch (error) {
            console.error("Error calling remoteSignout API:", error);
        } finally {
            // Always sign out locally regardless of API result
            await localSignOut({
                redirect: false,
            });
            router.push("/login");
        }
    };

    // Close menu after 10 seconds or when clicking outside
    useEffect(() => {
        if (isOpen) {
            const handleClickOutside = (event: MouseEvent) => {
                if (
                    menuRef.current &&
                    !menuRef.current.contains(event.target as Node)
                ) {
                    setTimeout(() => {
                        setIsOpen(false);
                    }, 200);
                }
            };

            document.addEventListener("mousedown", handleClickOutside);

            timeoutRef.current = setTimeout(() => {
                // setIsOpen(false);
            }, 10000);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                document.removeEventListener("mousedown", handleClickOutside);
            };
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        }
    }, [isOpen, setIsOpen]);

    const menuItems = [
        {
            label: "המפה העירונית",
            icon: "location",
            color: "#76C7C5",
            href: "/",
        },
        {
            label: "פרופיל אישי",
            icon: "person",
            image: user?.image,
            color: "#B0B0B0",
            href: "/user/profile",
        },
        {
            label: "פרופיל בית הספר",
            icon: "warehouse",
            color: "#f5fca2",
            href: "/school/profile",
            hidden:
                !user?.current_teacher?.school?.id ||
                !user?.current_teacher?.is_school_admin,
        },
        {
            label: "מורי בית הספר",
            icon: "user-add",
            color: "#a3f7b4",
            href: "/school/teachers",
            hidden:
                !user?.current_teacher?.school?.id ||
                !user?.current_teacher?.is_school_admin,
        },
        {
            label: "החלפת בית ספר",
            icon: "switch",
            color: "#76C7C5",
            href: "/user/school",
            hidden:
                (user?.teachers || []).filter(
                    (teacher) => teacher.currently_active
                ).length <= 1,
        },
        {
            label: "ממשק ניהול",
            icon: "settings",
            color: "#d4d4d4",
            href: "/admin",
            hidden: !user?.is_app_admin,
        },
        {
            label: "התנתקות",
            icon: "logout",
            color: "#F4C4D3",
            href: "#",
            onClick: handleLogout,
        },
    ] as {
        label: string;
        icon?: IconName;
        image?: string;
        color?: string;
        href?: string;
        onClick?: () => void;
        hidden?: boolean;
    }[];

    return (
        isOpen && (
            <>
                <div
                    ref={menuRef}
                    className={`menu`}
                    style={{
                        ...style,
                    }}
                >
                    {menuItems
                        .filter((item) => !item.hidden)
                        .map((item) => (
                            <Link
                                key={item.label}
                                href={item?.href || "#"}
                                onClick={() => {
                                    item?.onClick?.();
                                    setIsOpen(false);
                                }}
                            >
                                <div className="menu-item a-group">
                                    <div className="menu-icon relative">
                                        <div
                                            className="menu-icon-background"
                                            style={{
                                                backgroundColor: item.color,
                                                width: "20px",
                                                height: "20px",
                                            }}
                                        />
                                        <div className="menu-icon-circle relative z-100">
                                            <Icon
                                                name={"circle"}
                                                width={20}
                                                height={20}
                                            />
                                        </div>

                                        <div
                                            style={{
                                                right: item.image ? "0" : "5px",
                                            }}
                                        >
                                            {item.image ? (
                                                <Image
                                                    src={item.image}
                                                    alt={item.label}
                                                    width={20}
                                                    height={20}
                                                />
                                            ) : (
                                                item.icon && (
                                                    <Icon
                                                        name={item.icon}
                                                        width={10}
                                                        height={10}
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>
                                    {item.label}
                                </div>
                            </Link>
                        ))}
                </div>

                <div className="menu-overlay" />
            </>
        )
    );
};
